/**
 * Chatbot Service
 *
 * Handles communication with the chatbot API endpoints.
 * Uses fetch + ReadableStream for SSE streaming (not EventSource,
 * because we need POST method + Authorization headers).
 */

import { supabase } from '../utils/supabase';
import { Modo } from '../utils/modo';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ──────────────────────────────────────
// Types
// ──────────────────────────────────────

export interface ChatSession {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface CitationVerification {
  estado: 'verificada' | 'parcial' | 'no_verificada';
  score: number;
}

export interface Citation {
  document_id: string;
  titulo: string;
  tipo: string;
  numero?: string | null;
  year: number;
  institution_code?: string;
  excerpt?: string | null;
  verification?: CitationVerification;
  // M-7 (14-10): la ficha de la cita. Solo el motor nuevo los trae; un campo ausente no se muestra.
  resumen?: string | null;
  fecha?: string | null;
  source_url?: string | null;
}

export interface ChatMessageResponse {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations?: Citation[];
  created_at: string;
  // Línea al terminar el turno, «modo · N fuentes · N s» (utils/modo.ts). Solo la trae un envío en vivo;
  // el historial recargado no la tiene porque el servidor no la guarda.
  reasoningReceipt?: string;
}

export interface StreamCallbacks {
  onSession?: (sessionId: string) => void;
  onToolStart?: (tool: string, args: any) => void;
  onToolResult?: (tool: string, result: any) => void;
  /** Motor nuevo: etapa en curso (understand, search, read, write, verify). */
  onStage?: (etapa: string, detalle?: unknown) => void;
  onContentDelta?: (delta: string) => void;
  onContentDone?: (content: string, citations: Citation[]) => void;
  onError?: (message: string) => void;
  onDone?: () => void;
}

// ──────────────────────────────────────
// Auth helper
// ──────────────────────────────────────

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    return {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json'
    };
  }
  return { 'Content-Type': 'application/json' };
}

// ──────────────────────────────────────
// Main streaming function
// ──────────────────────────────────────

/**
 * Send a message and stream the response via SSE.
 * Uses fetch + ReadableStream (not EventSource) for POST + auth support.
 */
export async function sendMessage(
  sessionId: string | null,
  message: string,
  callbacks: StreamCallbacks,
  signal?: AbortSignal,
  modo?: Modo
): Promise<void> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/chat/message`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ session_id: sessionId, message, ...(modo ? { modo } : {}) }),
    signal
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    callbacks.onError?.(errorText);
    return;
  }

  if (!response.body) {
    callbacks.onError?.('No response body');
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    let eventCount = 0;
    let contentDeltaCount = 0;
    let doneReceived = false;

    const dispatchEvent = (event: any) => {
      eventCount++;
      switch (event.type) {
        case 'session':
          console.log('[SSE] session:', event.session_id);
          callbacks.onSession?.(event.session_id);
          break;
        case 'tool_start':
          console.log('[SSE] tool_start:', event.tool);
          callbacks.onToolStart?.(event.tool, event.args);
          break;
        case 'stage':
          callbacks.onStage?.(event.etapa, event.detalle);
          break;
        case 'router':
          // El motor avisa que pide una aclaración; la respuesta llega igual por content_done.
          break;
        case 'tool_result':
          console.log('[SSE] tool_result:', event.tool, event.result?.error ? 'ERROR' : 'OK');
          callbacks.onToolResult?.(event.tool, event.result);
          break;
        case 'content_delta':
          contentDeltaCount++;
          callbacks.onContentDelta?.(event.delta);
          break;
        case 'content_done':
          console.log('[SSE] content_done, citations:', event.citations?.length || 0, 'content length:', event.content?.length || 0);
          callbacks.onContentDone?.(event.content, event.citations || []);
          break;
        case 'error':
          console.error('[SSE] error:', event.message);
          callbacks.onError?.(event.message);
          break;
        case 'done':
          console.log('[SSE] done. Total events:', eventCount, 'content deltas:', contentDeltaCount);
          doneReceived = true;
          callbacks.onDone?.();
          break;
        default:
          console.warn('[SSE] unknown event type:', event.type);
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Split on double newline (SSE event boundary)
      const parts = buffer.split('\n\n');
      buffer = parts.pop() || ''; // Keep incomplete part

      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith('data: ')) continue;
        try {
          const event = JSON.parse(line.slice(6));
          dispatchEvent(event);
        } catch (parseError) {
          console.warn('[SSE] parse error on line:', line.substring(0, 100), parseError);
        }
      }
    }

    // Process any remaining buffer after stream ends
    if (buffer.trim()) {
      const remaining = buffer.trim();
      if (remaining.startsWith('data: ')) {
        try {
          const event = JSON.parse(remaining.slice(6));
          dispatchEvent(event);
        } catch (e) {
          console.warn('[SSE] parse error on remaining buffer:', remaining.substring(0, 100));
        }
      }
    }

    console.log('[SSE] stream ended. doneReceived:', doneReceived, 'events:', eventCount, 'deltas:', contentDeltaCount);

    // Safety net: if server never sent 'done' event, fire it now
    if (!doneReceived) {
      console.warn('[SSE] done event never received, firing safety net');
      callbacks.onDone?.();
    }
  } catch (error: any) {
    if (error.name === 'AbortError') {
      // User cancelled - not an error
      return;
    }
    callbacks.onError?.(error.message || 'Stream error');
  }
}

// ──────────────────────────────────────
// REST endpoints
// ──────────────────────────────────────

export async function listSessions(): Promise<ChatSession[]> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/chat/sessions`, { headers });
  const data = await response.json();
  return data.sessions || [];
}

export async function getSessionMessages(sessionId: string): Promise<{
  session: { id: string; title: string };
  messages: ChatMessageResponse[];
}> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/chat/sessions/${sessionId}/messages`, { headers });
  const data = await response.json();
  return { session: data.session, messages: data.messages || [] };
}

export async function deleteSession(sessionId: string): Promise<void> {
  const headers = await getAuthHeaders();
  await fetch(`${API_URL}/chat/sessions/${sessionId}`, {
    method: 'DELETE',
    headers
  });
}

// Default export for convenience
const chatbotService = {
  sendMessage,
  listSessions,
  getSessionMessages,
  deleteSession
};

export default chatbotService;
